/**
 * AdminCarerReview — identity/organisation screening review queue.
 *
 * "Pending" accounts were auto-flagged at registration/checkout by a
 * heuristic (missing personal name, business-style email/name/domain) —
 * never auto-rejected. Approve restores normal Carer_Free access;
 * Decline removes carer entitlements (audit record kept, never deleted;
 * any live Stripe subscription is flagged, never auto-cancelled here).
 *
 * The "Existing accounts" tab is a read-only report over members who
 * predate this system — never auto-restricted, for manual review only.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, ShieldAlert, History } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";

function relativeTime(iso) {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function MemberCard({ row, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`carer-review-row-${row.email}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-ink">
            {row.first_name || row.last_name ? `${row.first_name || ""} ${row.last_name || ""}`.trim() : (row.name || "(no name on file)")}
          </p>
          <p className="text-xs text-ink-soft mt-0.5">{row.email} · registered {relativeTime(row.created_at)}</p>
          <p className="text-xs text-ink-soft mt-0.5">Entitlements: {(row.entitlements || []).join(", ") || "none"}</p>
          {row.carer_declaration_accepted_at && (
            <p className="text-xs text-ink-soft mt-0.5">
              Declaration (v{row.carer_declaration_version}) accepted {relativeTime(row.carer_declaration_accepted_at)}
            </p>
          )}
          {!row.carer_declaration_accepted_at && (
            <p className="text-xs text-amber-700 mt-0.5">No declaration on record (pre-existing account)</p>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(row.carer_review_flag_labels || []).map((label) => (
              <span key={label} className="rounded-full bg-amber-100 text-amber-800 text-xs px-2.5 py-1">{label}</span>
            ))}
          </div>
          {row.carer_review_stripe_subscription_warning && (
            <p className="text-xs text-red-700 mt-2 font-medium" data-testid={`carer-review-stripe-warning-${row.email}`}>
              {row.carer_review_stripe_subscription_warning}
            </p>
          )}
        </div>
        <div className="shrink-0 flex gap-2">{children}</div>
      </div>
    </div>
  );
}

export default function AdminCarerReview() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState(null);
  const [existing, setExisting] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const [p, e] = await Promise.all([
        api.get("/api/admin/carer-review/pending"),
        api.get("/api/admin/carer-review/existing-report"),
      ]);
      setPending(p.data.members || []);
      setExisting(e.data.members || []);
      setError("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (email, action) => {
    setBusy((b) => ({ ...b, [email]: true }));
    try {
      await api.post(`/api/admin/carer-review/${encodeURIComponent(email)}/${action}`);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy((b) => ({ ...b, [email]: false }));
    }
  };

  return (
    <div data-testid="admin-carer-review-page">
      <PageHero
        eyebrow="JCC Admin"
        title="Carer identity review"
        description="Carer accounts (Carer Free, Carer Essentials, Pro Carer Office) are for individual carers only. Flagged registrations wait here for a routine review — never auto-rejected."
      />
      <section className="section pt-8">
        <div className="container-jcc max-w-5xl">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 mb-6" data-testid="admin-carer-review-error">
              {error}
            </div>
          )}

          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setTab("pending")}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === "pending" ? "bg-purple text-white" : "bg-slate-100 text-ink-soft"}`}
              data-testid="carer-review-tab-pending"
            >
              Awaiting review {pending ? `(${pending.length})` : ""}
            </button>
            <button
              type="button"
              onClick={() => setTab("existing")}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === "existing" ? "bg-purple text-white" : "bg-slate-100 text-ink-soft"}`}
              data-testid="carer-review-tab-existing"
            >
              Existing accounts report {existing ? `(${existing.length})` : ""}
            </button>
          </div>

          {tab === "pending" && (
            <>
              {pending === null && !error && <p className="text-ink-muted">Loading…</p>}
              {pending && pending.length === 0 && (
                <p className="text-ink-muted flex items-center gap-2" data-testid="carer-review-pending-empty">
                  <CheckCircle2 className="w-4 h-4 text-teal" /> No accounts currently awaiting review.
                </p>
              )}
              {pending && pending.length > 0 && (
                <div className="space-y-3">
                  {pending.map((row) => (
                    <MemberCard key={row.email} row={row}>
                      <button
                        type="button"
                        onClick={() => decide(row.email, "approve")}
                        disabled={busy[row.email]}
                        className="btn-outline text-teal border-teal/30"
                        data-testid={`carer-review-approve-${row.email}`}
                      >
                        <CheckCircle2 className="w-4 h-4" /> Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => decide(row.email, "decline")}
                        disabled={busy[row.email]}
                        className="btn-outline text-red-600 border-red-200"
                        data-testid={`carer-review-decline-${row.email}`}
                      >
                        <XCircle className="w-4 h-4" /> Decline
                      </button>
                    </MemberCard>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "existing" && (
            <>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-sm text-ink-soft mb-4 flex items-start gap-2">
                <History className="w-4 h-4 shrink-0 mt-0.5" />
                Read-only. These accounts predate identity screening and are NOT automatically restricted —
                use Approve/Decline below only after manual review.
              </div>
              {existing === null && !error && <p className="text-ink-muted">Loading…</p>}
              {existing && existing.length === 0 && (
                <p className="text-ink-muted flex items-center gap-2" data-testid="carer-review-existing-empty">
                  <CheckCircle2 className="w-4 h-4 text-teal" /> No existing accounts matched the organisational-account indicators.
                </p>
              )}
              {existing && existing.length > 0 && (
                <div className="space-y-3">
                  {existing.map((row) => (
                    <MemberCard key={row.email} row={row}>
                      <button
                        type="button"
                        onClick={() => decide(row.email, "approve")}
                        disabled={busy[row.email]}
                        className="btn-outline text-teal border-teal/30"
                        data-testid={`carer-review-existing-approve-${row.email}`}
                      >
                        <ShieldAlert className="w-4 h-4" /> Mark reviewed
                      </button>
                      <button
                        type="button"
                        onClick={() => decide(row.email, "decline")}
                        disabled={busy[row.email]}
                        className="btn-outline text-red-600 border-red-200"
                        data-testid={`carer-review-existing-decline-${row.email}`}
                      >
                        <XCircle className="w-4 h-4" /> Decline
                      </button>
                    </MemberCard>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
